import { InboxIcon, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Ticket = {
  id: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  lastMessageAt: string;
  createdAt: string;
};

type StatusFilter = "open" | "in_progress" | "resolved" | "closed" | "all";

export function SupportTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<StatusFilter>("open");

  function load() {
    setLoading(true);
    fetch(`${API}/api/admin/support-tickets`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTickets(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const filtered = tickets.filter((t) => {
    if (tab === "all") return true;
    return t.status === tab;
  });

  const openCount = tickets.filter((t) => t.status === "open").length;
  const inProgressCount = tickets.filter((t) => t.status === "in_progress").length;
  const resolvedCount = tickets.filter((t) => t.status === "resolved" || t.status === "closed").length;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Support
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Innkommende supporthenvendelser fra kunder
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Oppdater
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusFilter)}>
        <TabsList>
          <TabsTrigger value="open">Åpne ({openCount})</TabsTrigger>
          <TabsTrigger value="in_progress">Under behandling ({inProgressCount})</TabsTrigger>
          <TabsTrigger value="resolved">Løste ({resolvedCount})</TabsTrigger>
          <TabsTrigger value="all">Alle ({tickets.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <InboxIcon className="h-12 w-12 text-muted-foreground/20 mb-4" />
          <p className="text-sm font-medium text-muted-foreground">Ingen supporthenvendelser</p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y divide-border">
            {filtered.map((ticket) => (
              <div
                key={ticket.id}
                className="flex items-center gap-4 px-5 py-4 hover:bg-muted/20 transition-colors cursor-pointer"
                onClick={() => navigate(`/support/${ticket.id}`)}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 flex-shrink-0">
                  <InboxIcon className="h-4 w-4 text-blue-600" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-foreground text-sm truncate">{ticket.subject}</p>
                    <StatusBadge status={ticket.status} />
                    <PriorityBadge priority={ticket.priority} />
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {ticket.fromName ? `${ticket.fromName} <${ticket.fromEmail}>` : ticket.fromEmail}
                    </span>
                    <span className="text-muted-foreground/40 text-xs">·</span>
                    <span className="text-xs text-muted-foreground">{formatDate(ticket.lastMessageAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    open: { label: "Åpen", variant: "warning" },
    in_progress: { label: "Under behandling", variant: "secondary" },
    resolved: { label: "Løst", variant: "success" },
    closed: { label: "Lukket", variant: "muted" },
  };
  const { label, variant } = map[status] ?? { label: status, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    urgent: { label: "Urgent", variant: "destructive" },
    high: { label: "Høy", variant: "destructive" },
    normal: { label: "Normal", variant: "muted" },
    low: { label: "Lav", variant: "muted" },
  };
  const { label, variant } = map[priority] ?? { label: priority, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
